import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export type InvoiceTheme = 'light' | 'dark';

interface InvoiceThemeContextType {
  theme: InvoiceTheme;
  toggleTheme: () => void;
  setTheme: (theme: InvoiceTheme) => void;
}

const INVOICE_THEME_STORAGE_KEY = 'paygatex_invoice_theme';

const InvoiceThemeContext = createContext<InvoiceThemeContextType | undefined>(undefined);

export function useInvoiceTheme() {
  const context = useContext(InvoiceThemeContext);
  if (!context) {
    throw new Error('useInvoiceTheme must be used within InvoiceThemeProvider');
  }
  return context;
}

const getPreferredInvoiceTheme = (): InvoiceTheme => {
  const stored = localStorage.getItem(INVOICE_THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const applyDocumentTheme = (theme: InvoiceTheme) => {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
};

const restoreLightDocumentTheme = () => {
  const root = document.documentElement;
  root.classList.remove('dark');
  root.classList.add('light');
};

interface InvoiceThemeProviderProps {
  children: ReactNode;
}

export function InvoiceThemeProvider({ children }: InvoiceThemeProviderProps) {
  const [theme, setThemeState] = useState<InvoiceTheme>(() => getPreferredInvoiceTheme());

  useEffect(() => {
    applyDocumentTheme(theme);
    localStorage.setItem(INVOICE_THEME_STORAGE_KEY, theme);

    return () => {
      restoreLightDocumentTheme();
    };
  }, [theme]);

  const setTheme = useCallback((next: InvoiceTheme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  return (
    <InvoiceThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </InvoiceThemeContext.Provider>
  );
}
